// PostHog (WTK-728): feature flags, so a new feature can be shown to chosen people first, and page
// views. EU Cloud. Off unless VUE_APP_POSTHOG_KEY is set for the build.
//
// Privacy: persistence "memory" sets no cookie and stores nothing in the browser; autocapture,
// session recording and surveys are off. The logged-in user is identified by their WhereToKnow id
// with their e-mail (what flags target people by); the property being viewed goes to flag
// evaluation only.
import Vue from "vue";
import posthog from "posthog-js";
import { isStaging } from "./env";
import { isDeclaredFlag } from "@/config/featureFlags";

const KEY = (process.env.VUE_APP_POSTHOG_KEY || "").trim();
const HOST = (process.env.VUE_APP_POSTHOG_HOST || "https://eu.i.posthog.com").trim();

let started = false;
// Reactive, so a template reading isFeatureOn() updates once the flags arrive.
const state = Vue.observable({ enabled: {} });

export function startAnalytics(router) {
  if (!KEY || started) return;
  posthog.init(KEY, {
    api_host: HOST,
    persistence: "memory",
    person_profiles: "identified_only",
    autocapture: false,
    capture_pageview: false,
    capture_pageleave: false,
    disable_session_recording: true,
    // No in-app surveys: their script is not loaded at all.
    disable_surveys: true,
  });
  started = true;
  posthog.register({ environment: environment() });
  posthog.setPersonPropertiesForFlags({ environment: environment() }, false);
  posthog.onFeatureFlags((flags) => {
    const enabled = {};
    (flags || []).forEach((f) => {
      enabled[f] = true;
    });
    state.enabled = enabled;
  });
  // A single-page app: one page view per route.
  router.afterEach((to) => {
    posthog.capture("$pageview", { $current_url: window.location.origin + to.path, route: to.name || to.path });
  });
}

function environment() {
  return isStaging() ? "staging" : "production";
}

// After login (/auth/me): who is using the dashboard.
export function identifyUser(user) {
  if (!started || !user || !user.id) return;
  posthog.identify(String(user.id), { email: user.email, username: user.username });
}

// The property being viewed ({ entityType, entityId }, utils/libredeskEntity.js): flags can be
// switched on per hotel / restaurant / brand. Used for flag evaluation only.
export function setCurrentUnit(entity) {
  if (!started) return;
  const props = entity && entity.entityType && entity.entityType !== "NONE"
    ? { unit_type: entity.entityType, unit_id: String(entity.entityId) }
    : { unit_type: "NONE", unit_id: "" };
  posthog.setPersonPropertiesForFlags(props);
}

export function resetAnalytics() {
  if (!started) return;
  posthog.reset();
  state.enabled = {};
  posthog.setPersonPropertiesForFlags({ environment: environment() }, false);
}

export function flagOn(key) {
  if (process.env.NODE_ENV !== "production" && !isDeclaredFlag(key)) {
    // eslint-disable-next-line no-console
    console.warn(`Feature flag "${key}" is not declared in src/config/featureFlags.js (format wtk-<ticket>-<what>)`);
  }
  return !!state.enabled[key];
}
